/**
 * Export API Client
 *
 * API client for PDF and Excel exports.
 */

import { getApiBaseUrl, getAuthToken } from './api';

export interface ExportFilters {
  status?: string;
  managerId?: number;
  periodeStart?: string;
  periodeEnd?: string;
}

export const exportApi = {
  /**
   * Get PDF export URL for single evaluation
   */
  getEvaluationPdfUrl(evaluationId: number): string {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();
    return `${baseUrl}/export/evaluation/${evaluationId}/pdf?token=${token}`;
  },

  /**
   * Get HTML preview URL (for debugging)
   */
  getEvaluationHtmlUrl(evaluationId: number): string {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();
    return `${baseUrl}/export/evaluation/${evaluationId}/html?token=${token}`;
  },

  /**
   * Download PDF for single evaluation
   */
  async downloadEvaluationPdf(evaluationId: number): Promise<Blob> {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();

    const response = await fetch(`${baseUrl}/export/evaluation/${evaluationId}/pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('PDF export mislukt');
    }

    return response.blob();
  },

  /**
   * Download CSV export
   */
  async downloadCsv(filters?: ExportFilters): Promise<Blob> {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();

    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.managerId) params.append('managerId', filters.managerId.toString());
    if (filters?.periodeStart) params.append('periodeStart', filters.periodeStart);
    if (filters?.periodeEnd) params.append('periodeEnd', filters.periodeEnd);

    const url = `${baseUrl}/export/evaluations/csv${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('CSV export mislukt');
    }

    return response.blob();
  },

  /**
   * Download bulk PDF export
   */
  async downloadBulkPdf(evaluationIds: number[]): Promise<Blob> {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();

    const response = await fetch(`${baseUrl}/export/evaluations/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ evaluationIds }),
    });

    if (!response.ok) {
      throw new Error('Bulk PDF export mislukt');
    }

    return response.blob();
  },

  /**
   * Trigger file download in browser
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
